<div class="chat-calendar">
  <!-- Header / controls -->
  <div class="chat-calendar__header">
    <div class="chat-calendar__title">
      <h2>Chat History</h2>
      <span class="chat-calendar__subtitle">{{ headerLabel }}</span>
    </div>

    <div class="chat-calendar__controls">
      <button type="button" (click)="goPrev()" class="btn btn--ghost">&lt;</button>
      <button type="button" (click)="goToday()" class="btn btn--primary">Today</button>
      <button type="button" (click)="goNext()" class="btn btn--ghost">&gt;</button>

      <div class="chat-calendar__view-toggle" role="radiogroup">
        <button
          type="button"
          class="btn btn--segmented"
          [class.btn--segmented-active]="viewMode === 'week'"
          (click)="setViewMode('week')"
        >
          Week
        </button>
        <button
          type="button"
          class="btn btn--segmented"
          [class.btn--segmented-active]="viewMode === 'month'"
          (click)="setViewMode('month')"
        >
          Month
        </button>
      </div>
    </div>
  </div>

  <!-- Calendar grid -->
  <div class="chat-calendar__grid">
    <div class="chat-calendar__weekday-row">
      <div class="weekday" *ngFor="let w of ['Sun','Mon','Tue','Wed','Thu','Fri','Sat']">
        {{ w }}
      </div>
    </div>

    <div class="chat-calendar__days">
      <button
        *ngFor="let day of days; trackBy: trackByDayIndex"
        type="button"
        class="day-cell"
        [class.day-cell--today]="day.isToday"
        [class.day-cell--muted]="!day.inCurrentMonth && viewMode === 'month'"
        [class.day-cell--selected]="isSameDay(day.date, selectedDate)"
        (click)="selectDay(day)"
      >
        <div class="day-cell__label-row">
          <span class="day-cell__label">{{ day.label }}</span>
          <span class="day-cell__badge" *ngIf="day.hasConversations">
            {{ day.conversationCount }}
          </span>
          <span class="day-cell__badge day-cell__badge--notes" *ngIf="day.hasNotes">
            {{ day.notesCount }}
          </span>
        </div>

        <div class="day-cell__meta">
          <span *ngIf="day.messageCount === 1">{{ day.messageCount }} msg</span>
          <span *ngIf="day.messageCount > 1">{{ day.messageCount }} msgs</span>
        </div>
      </button>
    </div>
  </div>

  <!-- Content panels -->
  <div class="chat-calendar__content">
    <!-- Left: Conversations for selected day -->
    <section class="chat-calendar__day-panel">
      <header class="panel-header">
        <h3>{{ selectedDateLabel }}</h3>
        <p class="panel-header__desc">
          Conversations on this day from Firebase chat history.
        </p>
        <p class="panel-header__meta">
          {{ selectedDateSummary.conversationCount }}
          {{ selectedDateSummary.conversationCount === 1 ? 'conversation' : 'conversations' }}
          ·
          {{ selectedDateSummary.messageCount }}
          {{ selectedDateSummary.messageCount === 1 ? 'message' : 'messages' }}
        </p>
      </header>

      <ng-container *ngIf="conversationsForSelectedDate.length; else noConversations">
        <ul class="conversation-list">
          <li
            *ngFor="let conv of conversationsForSelectedDate; trackBy: trackByConvId"
            (click)="selectConversation(conv)"
            class="conversation-list__item"
            [class.conversation-list__item--active]="selectedConversation?.id === conv.id"
          >
            <div class="conversation-list__title">
              {{ conversationTopicLabel(conv) }}
            </div>

            <div class="conversation-list__meta">
              <span>
                {{ conv.messages?.length || 0 }}
                {{ (conv.messages?.length || 0) === 1 ? 'message' : 'messages' }}
              </span>
              <span class="conversation-list__timestamp">
                {{ formatConversationTimestamp(conv, 'local') }}
                <small class="conversation-list__timestamp-utc">
                  {{ formatConversationTimestamp(conv, 'utc') }}
                </small>
              </span>
            </div>
          </li>
        </ul>
      </ng-container>

      <ng-template #noConversations>
        <div class="empty-state">
          <p>No conversations for this day.</p>
          <p class="empty-state__hint">
            Pick a different date in the calendar to see your chat history.
          </p>
        </div>
      </ng-template>

      <div class="notes-panel">
        <h4>Calendar Notes</h4>
        <div class="notes-list" *ngIf="selectedDayNotes.length; else noNotes">
          <div class="notes-item" *ngFor="let note of selectedDayNotes">
            <div class="notes-item__meta">
              <span>{{ note.createdAt | date: 'shortTime' }}</span>
              <button type="button" (click)="deleteQuickNote(note)">✕</button>
            </div>
            <div class="notes-item__conversation" *ngIf="note.conversationTitle">
              <i class="fas fa-comments"></i>
              {{ note.conversationTitle }}
            </div>
            <p>{{ note.text }}</p>
          </div>
        </div>
        <ng-template #noNotes>
          <p class="notes-empty">No notes yet. Add one below.</p>
        </ng-template>

        <form class="notes-form" (ngSubmit)="addQuickNote()" autocomplete="off">
          <label>
            Conversation
            <select [(ngModel)]="newNoteConversationId" name="noteConversation">
              <option value="">No conversation</option>
              <option *ngFor="let conv of conversationsForSelectedDate" [value]="conv.id">
                {{ conversationTopicLabel(conv) }}
              </option>
            </select>
          </label>
          <textarea
            [(ngModel)]="newNoteText"
            name="noteText"
            rows="2"
            placeholder="Capture Calendar Note For This Day..."
          ></textarea>
          <div class="notes-form__actions">
            <button type="submit" [disabled]="!newNoteText.trim()">Add Note</button>
          </div>
        </form>
      </div>
    </section>

    <!-- Right: Messages in selected conversation -->
    <section class="chat-calendar__conversation-panel">
      <ng-container *ngIf="selectedConversation; else noConversationSelected">
        <header class="panel-header">
          <h3>{{ selectedConversation ? conversationTopicLabel(selectedConversation) : 'Conversation' }}</h3>
          <p class="panel-header__desc">
            Full message history for this conversation.
          </p>
        </header>

        <div class="conversation-messages">
          <div
            *ngFor="let msg of messagesForSelectedConversation; trackBy: trackByMsgId"
            class="message"
            [class.message--user]="msg.role === 'user'"
            [class.message--assistant]="msg.role === 'assistant'"
          >
            <div class="message__meta">
              <span class="message__role">
                {{ msg.role | titlecase }}
              </span>
              <span class="message__timestamp">
                {{ msg.timestamp | date: 'short' }}
              </span>
            </div>
            <div class="message__content">
              {{ msg.content }}
            </div>
            <button
              *ngIf="msg.role === 'user' || msg.role === 'assistant'"
              type="button"
              class="message-copy-btn"
              (click)="copyConversationMessage(msg)"
            >
              {{ copiedMessageId === (msg.id || '') ? 'Copied' : 'Copy' }}
            </button>
          </div>
        </div>
      </ng-container>

      <ng-template #noConversationSelected>
        <div class="empty-state">
          <p>Select a conversation from the list on the left.</p>
        </div>
      </ng-template>
    </section>
  </div>
</div>
